import type { Ref } from 'vue';
import type { AxiosResponse } from 'axios';
import type { MapElemInfo } from 'src/models';
import type { ShowImageParams } from 'src/models/top';
import useSwitchGeoItem from 'src/composables/useSwitchGeoItem';
import useSavedImage from 'src/composables/useSavedImage';

type MapElemInfoImagePreviewEmitFn<TElem> = {
  (event: 'show-previous-geo-item' | 'show-next-geo-item', elem: MapElemInfo<TElem>): void;
  (event: 'component-resized'): void;
  (event: 'show-image', params: ShowImageParams): void;
};

interface MapElemInfoImagePreviewParams<TElem, TData> {
  emit: MapElemInfoImagePreviewEmitFn<TElem>;
  elem: Ref<MapElemInfo<TElem>>;
  refMapElemInfoData: Ref<TData>;
  fetchApiFn: () => Promise<AxiosResponse<Blob>>;
  // 画像取得をスキップする条件を持たないコンポーネントは渡さない設計のため optional
  skipFetchingImage?: Ref<boolean>;
  modalTitle: Ref<string>;
  downloadFilenameTemplate: Ref<string>;
}

interface MapElemInfoImagePreviewContext {
  showPrevious: () => void;
  showNext: () => void;
  savedImage: Ref<string | null>;
  showImage: () => void;
}

// 保存画像プレビューと前後geoItemナビゲーションを持つMapElemInfo系コンポーネント向けに、
// useSwitchGeoItemとuseSavedImageへの同一形状の配線をまとめた composable。
export default function useMapElemInfoImagePreview<TElem, TData>(
  params: MapElemInfoImagePreviewParams<TElem, TData>,
): MapElemInfoImagePreviewContext {
  const { showPrevious, showNext } = useSwitchGeoItem(params.emit, params.elem);
  const { savedImage, showImage } = useSavedImage({
    refMapElemInfoData: params.refMapElemInfoData,
    emit: params.emit,
    fetchApiFn: params.fetchApiFn,
    skipFetchingImage: params.skipFetchingImage,
    modalTitle: params.modalTitle,
    downloadFilenameTemplate: params.downloadFilenameTemplate,
  });

  return {
    showPrevious,
    showNext,
    savedImage,
    showImage,
  };
}
